import React, { useEffect, useId, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import { communicationService } from '@/services/enhancedDatabaseService';
import type { Communication } from '@/types/database-enhancements';
import { useDebounce } from '@/hooks/useDebounce';
import { formatDistanceToNow } from 'date-fns';
import { Mail, MessageSquare, Phone, Calendar, Search, Filter, CheckCircle, Clock, XCircle, Eye, MousePointer, ArrowDownLeft, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { communicationStatusKey, communicationStatusesFor, isCommunicationStatusFilter } from './communicationStatus';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const COMMUNICATION_TYPES: readonly string[] = ['email', 'sms', 'call', 'meeting'];

interface PageRef {
  key: string;
  page: number;
}

interface LoadedPage extends PageRef {
  rows: Communication[];
  total: number;
  overall: number;
}

const isOutOfRange = (error: unknown) =>
  typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 'PGRST103';

export const CommunicationsDashboard: React.FC = () => {
  const titleId = useId();
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    searchTerm: ''
  });
  const debouncedSearch = useDebounce(filters.searchTerm, SEARCH_DEBOUNCE_MS);
  const queryKey = JSON.stringify([filters.type, filters.status, debouncedSearch]);

  // A new query always starts on page 1, and the reset is stored so returning to an
  // earlier query never brings back its old page.
  const [paging, setPaging] = useState<PageRef>({ key: queryKey, page: 1 });
  if (paging.key !== queryKey) {
    setPaging({ key: queryKey, page: 1 });
  }
  const page = paging.key === queryKey ? paging.page : 1;

  const [reloadToken, setReloadToken] = useState(0);
  // Only successful responses are committed; everything on screen derives from them.
  const [result, setResult] = useState<LoadedPage | null>(null);
  const [failed, setFailed] = useState<PageRef | null>(null);
  const [fetching, setFetching] = useState(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    const isCurrent = () => requestId === requestIdRef.current;
    const [type, status, search] = JSON.parse(queryKey) as [string, string, string];
    const filtered = type !== 'all' || status !== 'all' || search !== '';
    let recovering = false;
    setFetching(true);

    void (async () => {
      try {
        const [pageResult, overall] = await Promise.all([
          communicationService.getCommunicationPage({
            page,
            pageSize: PAGE_SIZE,
            type: COMMUNICATION_TYPES.includes(type) ? (type as Communication['type']) : undefined,
            statuses: isCommunicationStatusFilter(status) ? communicationStatusesFor(status) : undefined,
            search
          }),
          filtered ? communicationService.countCommunications() : Promise.resolve(null)
        ]);
        if (!isCurrent()) {
return;
}
        if (page > 1 && pageResult.rows.length === 0) {
          recovering = true;
          setPaging({ key: queryKey, page: 1 });
          return;
        }
        setResult({
          key: queryKey,
          page,
          rows: pageResult.rows,
          total: pageResult.total,
          overall: overall ?? pageResult.total
        });
        setFailed(null);
      } catch (error) {
        if (!isCurrent()) {
return;
}
        if (page > 1 && isOutOfRange(error)) {
          recovering = true;
          setPaging({ key: queryKey, page: 1 });
          return;
        }
        console.error('Error loading communications:', error);
        toast.error('Failed to load communications');
        setFailed({ key: queryKey, page });
      } finally {
        if (isCurrent() && !recovering) {
          setFetching(false);
        }
      }
    })();

    return () => {
      requestIdRef.current += 1;
    };
  }, [queryKey, page, reloadToken]);

  const getIcon = (type: Communication['type']) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'sms': return <MessageSquare className="h-4 w-4" />;
      case 'call': return <Phone className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      default: return null;
    }
  };

  const getStatusBadge = (comm: Communication) => {
    const status = communicationStatusKey(comm.status);

    if (status === 'sent') {
      return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-800"><CheckCircle className="h-3 w-3 mr-1" />Sent</Badge>;
    } else if (status === 'delivered') {
      return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Delivered</Badge>;
    } else if (status === 'opened') {
      return <Badge variant="outline" className="border-purple-200 bg-purple-50 text-purple-800"><Eye className="h-3 w-3 mr-1" />Opened</Badge>;
    } else if (status === 'clicked') {
      return <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-indigo-800"><MousePointer className="h-3 w-3 mr-1" />Clicked</Badge>;
    } else if (status === 'failed') {
      return <Badge variant="outline" className="border-transparent bg-destructive text-destructive-foreground"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
    } else if (status === 'received') {
      return <Badge variant="outline"><ArrowDownLeft className="h-3 w-3 mr-1" />Received</Badge>;
    } else if (status === 'pending') {
      return <Badge variant="outline" className="border-transparent bg-secondary text-secondary-foreground"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    } else {
      return <Badge variant="outline" className="capitalize">{comm.status || 'Unknown'}</Badge>;
    }
  };

  const getContact = (comm: Communication) => {
    const contact = comm.email_address || comm.phone_number;
    if (!contact) {
      return '—';
    }
    return (
      <>
        <span className="text-muted-foreground">{comm.direction === 'inbound' ? 'From ' : 'To '}</span>
        {contact}
      </>
    );
  };

  if (result === null && failed === null) {
    return (
      <div className="flex items-center justify-center p-8" role="status">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-hidden="true"></div>
        <span className="sr-only">Loading communications</span>
      </div>
    );
  }

  const current = result && result.key === queryKey && result.page === page ? result : null;
  const failure = !current && failed !== null && failed.key === queryKey && failed.page === page;
  // While a new page or filter loads, the last committed results stay on screen.
  const shown = current ?? (failure ? null : result);
  const totalPages = shown ? Math.ceil(shown.total / PAGE_SIZE) : 0;
  const firstRow = shown ? (shown.page - 1) * PAGE_SIZE + 1 : 0;
  const lastRow = shown ? firstRow + shown.rows.length - 1 : 0;
  const goToPage = (next: number) => setPaging({ key: queryKey, page: next });

  let statusText = '';
  if (shown && shown.total > 0) {
    statusText = `Showing ${firstRow.toLocaleString()}–${lastRow.toLocaleString()} of ${shown.total.toLocaleString()}`;
  } else if (shown) {
    statusText = 'No communications found';
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Communications</h1>
        <p className="text-muted-foreground">Track all email, SMS, and other communications</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle id={titleId}>Communication History</CardTitle>
              {shown && (
                <CardDescription>
                  {shown.total.toLocaleString()} of {shown.overall.toLocaleString()} communications
                </CardDescription>
              )}
            </div>
            <Button onClick={() => setReloadToken((token) => token + 1)}>
              {fetching && <Loader2 aria-hidden="true" className="motion-safe:animate-spin" />}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-4">
            <div className="min-w-0 lg:flex-1">
              <Label htmlFor="search" className="sr-only">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search communications..."
                  value={filters.searchTerm}
                  onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 lg:flex lg:gap-4">
              <Select
                value={filters.type}
                onValueChange={(value) => setFilters({ ...filters, type: value })}
              >
                <SelectTrigger aria-label="Filter by type" className="w-full lg:w-[150px]">
                  <Filter className="mr-2 hidden h-4 w-4 lg:block" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="call">Call</SelectItem>
                  <SelectItem value="meeting">Meeting</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger aria-label="Filter by status" className="w-full lg:w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="opened">Opened</SelectItem>
                  <SelectItem value="clicked">Clicked</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <Table
            aria-labelledby={titleId}
            containerProps={{
              role: 'region',
              'aria-label': 'Communication history table',
              'aria-busy': fetching,
              tabIndex: 0,
              className: 'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
            }}
          >
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Type</TableHead>
                <TableHead scope="col">Subject/Content</TableHead>
                <TableHead scope="col">Contact</TableHead>
                <TableHead scope="col">Status</TableHead>
                <TableHead scope="col">Engagement</TableHead>
                <TableHead scope="col">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {failure ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Couldn't load communications. Refresh to try again.
                  </TableCell>
                </TableRow>
              ) : !shown ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Loading communications…
                  </TableCell>
                </TableRow>
              ) : shown.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No communications found
                  </TableCell>
                </TableRow>
              ) : (
                shown.rows.map((comm) => (
                  <TableRow key={comm.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getIcon(comm.type)}
                        <span className="capitalize">{comm.type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{comm.subject || 'No subject'}</p>
                        {comm.content && (
                          <p className="text-sm text-muted-foreground truncate max-w-xs">
                            {comm.content}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getContact(comm)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(comm)}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {comm.delivered_at && (
                          <p>Delivered: {formatDistanceToNow(new Date(comm.delivered_at))} ago</p>
                        )}
                        {comm.opened_at && (
                          <p className="text-purple-600">Opened: {formatDistanceToNow(new Date(comm.opened_at))} ago</p>
                        )}
                        {comm.clicked_at && (
                          <p className="text-indigo-600">Clicked: {formatDistanceToNow(new Date(comm.clicked_at))} ago</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatDistanceToNow(new Date(comm.created_at))} ago
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p role="status" className={shown && shown.total === 0 ? 'sr-only' : 'text-sm text-muted-foreground'}>
              {failure ? '' : statusText}
            </p>
            {!failure && totalPages > 1 && shown && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => goToPage(shown.page - 1)}
                  disabled={fetching || shown.page <= 1}
                >
                  <ChevronLeft aria-hidden="true" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => goToPage(shown.page + 1)}
                  disabled={fetching || shown.page >= totalPages}
                >
                  Next
                  <ChevronRight aria-hidden="true" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
